import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useStudioClient } from '../context';
import { parseApiError } from '../errors';

export interface UseProductsOptions {
  cursor?: string;
  limit?: number;
  platformId?: 'wix' | 'etsy' | 'ebay' | 'amazon' | 'shopify' | 'woocommerce';
  status?: 'active' | 'draft' | 'archived';
  query?: string;
  /** Skip the request entirely (e.g. while waiting for a workspace). */
  enabled?: boolean;
}

/**
 * `GET /api/products` — paginated list of `UniversalProduct` for the
 * authenticated workspace. Filters mirror the backend's
 * `ProductsQuerySchema` from `@studio/contract`.
 *
 * `keepPreviousData` so cursor-paged navigation doesn't blank the grid.
 */
export function useProducts(options: UseProductsOptions = {}) {
  const client = useStudioClient();
  const { enabled = true, cursor, limit, platformId, status, query } = options;

  return useQuery({
    queryKey: ['products', { cursor, limit, platformId, status, query }],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const search: Record<string, string> = {};
      if (cursor !== undefined) search.cursor = cursor;
      if (limit !== undefined) search.limit = String(limit);
      if (platformId !== undefined) search.platformId = platformId;
      if (status !== undefined) search.status = status;
      if (query !== undefined) search.query = query;
      const res = await client.api.products.$get({ query: search });
      if (!res.ok) throw await parseApiError(res);
      return res.json();
    },
  });
}
